import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OrangeMoneyPayment from './OrangeMoneyPayment';
import ParentPaymentDialog from './ParentPaymentDialog';
import BulkInvoiceDialog from './BulkInvoiceDialog';

// Icons
import { CreditCard, Euro, FileText, Download, Plus, Calendar, CheckCircle, XCircle, Clock, AlertTriangle, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingManagement = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  
  // Dialog states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [bulkInvoiceDialogOpen, setBulkInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [invoiceForm, setInvoiceForm] = useState({
    student_id: '',
    title: '',
    description: '',
    total_amount: '',
    due_date: '',
    periodicity: 'MONTHLY'
  });

  const [paymentForm, setPaymentForm] = useState({
    method: 'CARD',
    card_number: '',
    expiry_date: '',
    cvv: '',
    cardholder_name: '',
    phone_number: ''
  });

  const invoiceStatuses = [
    { value: 'DRAFT', label: 'Brouillon', color: 'badge-secondary', icon: FileText },
    { value: 'SENT', label: 'Envoyée', color: 'badge-primary', icon: Clock },
    { value: 'PAID', label: 'Payée', color: 'badge-success', icon: CheckCircle },
    { value: 'OVERDUE', label: 'En retard', color: 'badge-danger', icon: AlertTriangle },
    { value: 'CANCELLED', label: 'Annulée', color: 'badge-secondary', icon: XCircle }
  ];

  const periodicityOptions = [
    { value: 'MONTHLY', label: 'Mensuelle' },
    { value: 'QUARTERLY', label: 'Trimestrielle' },
    { value: 'BIANNUAL', label: 'Semestrielle' },
    { value: 'ANNUAL', label: 'Annuelle' }
  ];

  const paymentMethods = [
    { value: 'CARD', label: 'Carte bancaire' },
    { value: 'TRANSFER', label: 'Virement bancaire' },
    { value: 'CHECK', label: 'Chèque' },
    { value: 'CASH', label: 'Espèces' },
    { value: 'ORANGE_MONEY', label: 'Orange Money' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Recharger les paiements quand on accède à l'onglet "payments"
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPaymentsData();
    }
  }, [activeTab]);

  const fetchPaymentsData = async () => {
    try {
      const response = await axios.get(`${API}/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      const promises = [
        axios.get(`${API}/invoices`),
        axios.get(`${API}/financial/statistics`),
        axios.get(`${API}/payments`)
      ];

      if (user.role !== 'PARENT' && user.role !== 'STUDENT') {
        // Utiliser /api/students au lieu de /api/users pour avoir le student_id correct
        promises.push(axios.get(`${API}/students`));
      }

      const results = await Promise.all(promises);

      setInvoices(results[0].data);
      setStatistics(results[1].data);
      setPayments(results[2].data);
      
      if (results[3]) {
        setStudents(results[3].data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/invoices`, {
        ...invoiceForm,
        total_amount: parseFloat(invoiceForm.total_amount),
        due_date: new Date(invoiceForm.due_date).toISOString()
      });

      setInvoices(prev => [response.data, ...prev]);
      toast.success('Facture créée avec succès');
      resetInvoiceForm();
      setInvoiceDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création de la facture');
      console.error('Error creating invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (paymentForm.method === 'ORANGE_MONEY') {
        // Orange Money payment
        const paymentRequest = {
          invoice_id: selectedInvoice.id,
          amount: selectedInvoice.total_amount,
          phone_number: paymentForm.phone_number,
          payment_method: 'orange_money'
        };

        const response = await axios.post(`${API}/payment/initiate`, paymentRequest);
        
        if (response.data.status === 'SUCCESSFUL') {
          // Update invoice in local state
          setInvoices(prev => 
            prev.map(inv => 
              inv.id === selectedInvoice.id 
                ? { ...inv, status: 'PAID', payment_date: new Date().toISOString(), payment_method: paymentForm.method }
                : inv
            )
          );
          toast.success(`Paiement Orange Money réussi! Transaction: ${response.data.transaction_id}`);
        } else {
          toast.error('Échec du paiement Orange Money');
        }
      } else {
        // Traditional payment processing (existing logic)
        await axios.put(`${API}/invoices/${selectedInvoice.id}/pay`, paymentForm);

        // Update invoice in local state
        setInvoices(prev => 
          prev.map(inv => 
            inv.id === selectedInvoice.id 
              ? { ...inv, status: 'PAID', payment_date: new Date().toISOString(), payment_method: paymentForm.method }
              : inv
          )
        );
        toast.success('Paiement effectué avec succès');
      }

      resetPaymentForm();
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors du paiement';
      toast.error(errorMsg);
      console.error('Error processing payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      student_id: '',
      title: '',
      description: '',
      total_amount: '',
      due_date: '',
      periodicity: 'MONTHLY'
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      method: 'CARD',
      card_number: '',
      expiry_date: '',
      cvv: '',
      cardholder_name: '',
      phone_number: ''
    });
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève inconnu';
  };

  const getStatusInfo = (status) => {
    return invoiceStatuses.find(s => s.value === status) || invoiceStatuses[0];
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    resetPaymentForm();
    setPaymentDialogOpen(true);
  };

  const canManageBilling = ['ADMIN', 'ACCOUNTANT', 'PRINCIPAL'].includes(user.role);
  const canViewInvoices = user.role === 'PARENT' || canManageBilling;

  if (loading && invoices.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="billing-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <CreditCard className="w-6 h-6 mr-2 text-blue-600" />
            {user.role === 'PARENT' ? 'Mes Factures' : 'Facturation'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user.role === 'PARENT' 
              ? 'Consultez et payez vos factures scolaires'
              : 'Gestion de la facturation et des paiements'
            }
          </p>
        </div>

        {canManageBilling && (
          <>
            <div className="flex space-x-2">
              <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-invoice-btn"
                    onClick={() => { resetInvoiceForm(); setInvoiceDialogOpen(true); }}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle Facture
                  </Button>
                </DialogTrigger>

            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer une facture</DialogTitle>
                <DialogDescription>
                  Générez une nouvelle facture pour un élève
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleInvoiceSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="student">Élève</Label>
                  <Select 
                    value={invoiceForm.student_id} 
                    onValueChange={(value) => setInvoiceForm({ ...invoiceForm, student_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Objet de la facture</Label>
                  <Input
                    id="title"
                    value={invoiceForm.title}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, title: e.target.value })}
                    placeholder="ex: Frais de scolarité T1, Sortie scolaire..."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnelle)</Label>
                  <Textarea
                    id="description"
                    value={invoiceForm.description}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                    placeholder="Détails de la facturation..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_amount">Montant (GNF)</Label>
                    <Input
                      id="total_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceForm.total_amount}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, total_amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="due_date">Date d'échéance</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="periodicity">Périodicité</Label>
                  <Select 
                    value={invoiceForm.periodicity} 
                    onValueChange={(value) => setInvoiceForm({ ...invoiceForm, periodicity: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez la périodicité" />
                    </SelectTrigger>
                    <SelectContent>
                      {periodicityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setInvoiceDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Création...' : 'Créer la facture'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button 
            data-testid="create-bulk-invoice-btn"
            onClick={() => setBulkInvoiceDialogOpen(true)}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Users className="w-4 h-4 mr-2" />
            Factures Groupées
          </Button>
        </div>
        </>
        )}
      </div>

      {/* Statistics */}
      {Object.keys(statistics).length > 0 && canManageBilling && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Factures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures émises</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Payées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statistics.paid_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures payées</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">En Attente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.pending_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">En attente de paiement</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">En Retard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{statistics.overdue_invoices}</div>
              <p className="text-xs text-gray-600 mt-1">Factures en retard</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Chiffre d'Affaires</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{statistics.total_revenue.toLocaleString()} GNF</div>
              <p className="text-xs text-gray-600 mt-1">Revenus total</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Taux de Paiement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-indigo-600">{statistics.payment_rate}%</div>
              <p className="text-xs text-gray-600 mt-1">Factures payées</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="invoices">Factures</TabsTrigger>
          <TabsTrigger value="payments">Historique Paiements</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          {invoices.length > 0 ? (
            <div className="space-y-4">
              {invoices.map((invoice) => {
                const statusInfo = getStatusInfo(invoice.status);
                const StatusIcon = statusInfo.icon;
                const isOverdue = invoice.status === 'SENT' && new Date(invoice.due_date) < new Date();

                return (
                  <Card key={invoice.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg">{invoice.title}</h3>
                            <Badge className={statusInfo.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                            {isOverdue && (
                              <Badge className="badge-danger">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                En retard
                              </Badge>
                            )}
                          </div>

                          {canManageBilling && (
                            <p className="text-sm text-gray-600 mb-2">
                              Élève: {getStudentName(invoice.student_id)}
                            </p>
                          )}

                          <p className="text-sm text-gray-600 mb-2">
                            N° Facture: {invoice.invoice_number}
                          </p>

                          {invoice.description && (
                            <p className="text-sm text-gray-700 mb-3">
                              {invoice.description}
                            </p>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-2xl font-bold text-blue-600">
                              {invoice.total_amount.toLocaleString()} GNF
                            </div>

                            <div className="text-right">
                              <div className="text-sm text-gray-600">
                                Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}
                              </div>
                              {invoice.payment_date && (
                                <div className="text-sm text-green-600">
                                  Payée le {new Date(invoice.payment_date).toLocaleDateString('fr-FR')}
                                </div>
                              )}
                            </div>
                          </div>

                          {(invoice.status === 'SENT' || isOverdue) && (
                            <div className="mt-4 flex space-x-3">
                              <Button
                                onClick={() => openPaymentDialog(invoice)}
                                className="btn-success"
                                size="sm"
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Payer maintenant
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger PDF
                              </Button>
                            </div>
                          )}

                          {invoice.status === 'PAID' && (
                            <div className="mt-4">
                              <Button variant="outline" size="sm">
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger reçu
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune facture</h3>
              <p className="text-gray-600">
                {user.role === 'PARENT' 
                  ? 'Vous n\'avez aucune facture en attente.'
                  : 'Aucune facture n\'a été créée.'
                }
              </p>
            </div>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique des Paiements</CardTitle>
              <CardDescription>Liste de tous les paiements enregistrés</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-12">
                  <Euro className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement</h3>
                  <p className="text-gray-600">
                    Aucun paiement n'a encore été enregistré
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Élève
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Montant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Méthode
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Référence
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => {
                        const student = students.find(s => s.id === payment.student_id);
                        const paymentDate = payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR') : '-';
                        const paymentMethod = payment.payment_method || 'N/A';
                        
                        return (
                          <tr key={payment.payment_id || payment.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {paymentDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {student ? `${student.first_name} ${student.last_name}` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.amount ? payment.amount.toLocaleString() : '0'} FG
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {paymentMethod === 'CASH' && '💵 Espèces'}
                              {paymentMethod === 'CHECK' && '📝 Chèque'}
                              {paymentMethod === 'BANK_TRANSFER' && '🏦 Virement'}
                              {paymentMethod === 'ORANGE_MONEY' && '🍊 Orange Money'}
                              {paymentMethod === 'CARD' && '💳 Carte'}
                              {!['CASH', 'CHECK', 'BANK_TRANSFER', 'ORANGE_MONEY', 'CARD'].includes(paymentMethod) && paymentMethod}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {payment.reference || payment.transaction_id || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={payment.status === 'SUCCESSFUL' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {payment.status === 'SUCCESSFUL' ? 'Réussi' : payment.status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog - New Multi-Method Payment */}
      <ParentPaymentDialog
        invoice={selectedInvoice}
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedInvoice(null);
        }}
        onPaymentSubmitted={() => {
          fetchInitialData();
          toast.success('Paiement enregistré avec succès!');
        }}
      />
      
      <BulkInvoiceDialog
        open={bulkInvoiceDialogOpen}
        onClose={() => setBulkInvoiceDialogOpen(false)}
        onSuccess={() => {
          fetchInitialData();
          toast.success('Factures créées avec succès!');
        }}
      />
    </div>
  );
};

export default BillingManagement;